import * as React from "react";
import { Button, Divider, Grid, Message, Modal } from "semantic-ui-react";

import { useDispatch } from "@teamprevent/admin-redux";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { add, isEqual, isSameDay } from "date-fns";

import { Clinic } from "@teamprevent/common/calendar/models";

import { cloneClinic } from "../actions";
import { CloneClinicResult } from "../models";
import { CLONE_CLINIC } from "../actionTypes";

import { ClinicRecurrence } from "./ClinicRecurrence";
import { AllAssociatedBlockedTimeList } from "./AllAssociatedBlockedTimeList";
import { CloneClinicResultModal } from "./CloneClinicResultModal";

interface Props {
    clinic: Clinic;
    onClose: () => void;
}

export const CloneClinicModal: React.FunctionComponent<Props> = ({ clinic, onClose }) => {
    const dispatch = useDispatch();

    const [recurrences, setRecurrences] = React.useState<(Date | null)[]>([null]);
    const [submitted, setSubmitted] = React.useState(false);
    const [cloneResults, setCloneResults] = React.useState<CloneClinicResult[]>([]);
    const [blockedTimeIdsToClone, setBlockedTimeIdsToClone] = React.useState<string[]>([]);

    const blockedTimes = React.useMemo(() => clinic.appointments?.filter(appointment => !appointment.ohCaseId && !appointment.clinicType), [clinic]) ?? [];
    const nonNullRecurrences = React.useMemo(() => recurrences.filter((r): r is Date => !!r), [recurrences]);
    const showSuccessCloneResult = React.useMemo(() =>
        cloneResults.length > 0 &&
        cloneResults.every(r => r.roomClashIds.length === 0 && r.teamPreventUserClashIds.length === 0), [cloneResults]);

    const changeRecurrenceDate = React.useCallback((date: Date, index: number) => {
        setRecurrences(recurrences.map((r, i) => index === i ? date : r));
        setSubmitted(false);
    }, [recurrences]);

    const addRecurrence = React.useCallback(() => {
        setSubmitted(false);
        setRecurrences(prevState => [...prevState, null]);
    }, []);

    const onSave = React.useCallback(async () => {
        setSubmitted(true);
        setCloneResults([]);

        if (recurrences.some(r => !r) || recurrences.length === 0) {
            return;
        }

        const recurrenceDates = nonNullRecurrences.map(date => date.toString());
        const sameDatesExist = new Set(recurrenceDates).size !== recurrenceDates.length;
        if (sameDatesExist) {
            return;
        }

        const result: CloneClinicResult[] = await dispatch(cloneClinic({
            clinicId: clinic.id,
            dates: nonNullRecurrences.map(d => ({
                start: add(d, {
                    hours: clinic.start?.hour(),
                    minutes: clinic.start?.minute(),
                }),
                end: add(d, {
                    hours: clinic.end?.hour(),
                    minutes: clinic.end?.minute(),
                })
            })),
            blockedTimeIds: blockedTimeIdsToClone
        }));

        setCloneResults(result);

        if (result.some(r => r.roomClashIds.length || r.teamPreventUserClashIds.length)) {
            return;
        }

    }, [blockedTimeIdsToClone, dispatch, nonNullRecurrences, clinic, recurrences]);

    const duplicateClone = (index: number, date: Date | null): boolean => {
        if (date) {
            return nonNullRecurrences.filter((d, i) => i !== index && d !== null).some(d => isEqual(d, date));
        }

        return false;
    };

    const updateBlockedTimesToClone = React.useCallback((blockedTimeId: string) => {
        const blockedTimeIdIndex = blockedTimeIdsToClone.indexOf(blockedTimeId);
        setBlockedTimeIdsToClone(prevState => (blockedTimeIdIndex > -1)
            ? blockedTimeIdsToClone.filter(id => id !== blockedTimeId)
            : [...prevState, blockedTimeId]);
    }, [blockedTimeIdsToClone]);

    if (showSuccessCloneResult)
    {
        return <CloneClinicResultModal cloneResults={cloneResults} onClose={onClose} />;
    }

    return (
        <Modal open>
            <Modal.Header content="Clone Clinic" />
            <Modal.Content>
                <Grid>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Message className="book-tasks-info">Please select any associated blocked time that you would also like to clone.</Message>
                        </Grid.Column>
                    </Grid.Row>
                    <AllAssociatedBlockedTimeList
                        blockedTimes={blockedTimes}
                        selectOrDeselectBlockedTime={updateBlockedTimesToClone}
                    />
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Divider />
                        </Grid.Column>
                    </Grid.Row>
                    {recurrences.map((r, index) => (
                        <ClinicRecurrence
                            key={index}
                            date={r && new Date(r.valueOf())}
                            onDateChanged={(date) => changeRecurrenceDate(date, index)}
                            onDelete={() => setRecurrences(recurrences.filter((d, i) => i !== index))}
                            cloneResult={cloneResults?.find(c => r && isSameDay(c.date, r))}
                            duplicateClone={duplicateClone(index, r)}
                            submitted={submitted}
                        />
                    ))}
                    <Grid.Row>
                        <Grid.Column>
                            <Button icon="add" onClick={addRecurrence} />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Modal.Content>
            <Modal.Actions>
                <Button content="Cancel" onClick={onClose} />
                <IsLoadingButton
                    requestNames={CLONE_CLINIC}
                    content="Save"
                    onClick={onSave}
                />
            </Modal.Actions>
        </Modal>
    );
};
